/**
 * Convert a schema FieldDef's validation config into react-hook-form
 * RegisterOptions rules.
 *
 * The returned object is spread into register(name, rules) calls.
 */

import type { RegisterOptions } from 'react-hook-form';
import type { FieldDef } from '../types';
import { isValidDateForType } from './calendarDate';

/**
 * Build react-hook-form validation rules from a schema field definition.
 *
 * @param field  - The field schema definition
 * @param active - Whether the field is currently visible (conditional fields
 *                 that are hidden should not be validated — pass false to
 *                 skip required validation when field is hidden)
 */
export function buildValidationRules(
  field: FieldDef,
  active = true,
): RegisterOptions {
  const rules: RegisterOptions = {};

  // Required
  if (field.required && active) {
    rules.required = `${resolveDefaultLabel(field)} is required`;
  }

  const overrides = field.validation_overrides ?? {};

  // Text / long_text: max_length
  if (field.type === 'text' || field.type === 'long_text' || field.type === 'passport_number') {
    const maxLen =
      overrides.max_length ??
      (field.type === 'long_text' ? 4000 : field.type === 'passport_number' ? 30 : 200);
    if (maxLen) {
      rules.maxLength = {
        value: maxLen,
        message: `Maximum ${maxLen} characters`,
      };
    }
  }

  // Email format
  if (field.type === 'email') {
    rules.pattern = {
      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: 'Enter a valid email address',
    };
  }

  // Phone: basic international format
  if (field.type === 'phone') {
    rules.pattern = {
      value: /^\+?[0-9\s\-().]{7,20}$/,
      message: 'Enter a valid phone number',
    };
  }

  // Date: ISO yyyy-mm-dd
  if (field.type === 'date') {
    rules.pattern = {
      value: /^\d{4}-\d{2}-\d{2}$/,
      message: 'Enter the date as DD/MM/YYYY',
    };
  }

  // Date partial: accepts yyyy-mm-dd, yyyy-mm, or yyyy
  if (field.type === 'date_partial') {
    rules.pattern = {
      value: /^\d{4}(-\d{2}(-\d{2})?)?$/,
      message: 'Enter month and year as MM/YYYY (or just the year)',
    };
  }

  // Date fields: reject impossible calendar dates (e.g. 1990-13-45, 2021-02-30)
  // that pass the shape pattern above, and optionally constrain relative to
  // today. Registered as named validators so both checks can run.
  if (field.type === 'date' || field.type === 'date_partial') {
    const dateType = field.type;
    const validators: Record<string, (value: unknown) => string | true> = {
      calendar: (value: unknown) => {
        if (!value || typeof value !== 'string') return true; // empty handled by required
        return isValidDateForType(value, dateType) || 'Enter a real calendar date';
      },
    };

    if (overrides.date_range) {
      const range = overrides.date_range;
      validators.range = (value: unknown) => {
        if (!value || typeof value !== 'string') return true; // empty handled by required
        const d = new Date(value.length === 4 ? `${value}-01-01` : value);
        if (Number.isNaN(d.getTime())) return true; // format handled by pattern
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        d.setHours(0, 0, 0, 0);
        if (range === 'future' && d <= today) return 'Date must be in the future';
        if (range === 'past' && d >= today) return 'Date must be in the past';
        if (range === 'not_future' && d > today) return 'Date cannot be in the future';
        if (range === 'not_past' && d < today) return 'Date cannot be in the past';
        return true;
      };
    }

    rules.validate = validators;
  }

  // Number: min / max
  if (field.type === 'number') {
    if (overrides.min !== undefined) {
      rules.min = { value: overrides.min, message: `Minimum value is ${overrides.min}` };
    }
    if (overrides.max !== undefined) {
      rules.max = { value: overrides.max, message: `Maximum value is ${overrides.max}` };
    }
  }

  // Custom regex pattern override
  if (overrides.pattern) {
    try {
      rules.pattern = {
        value: new RegExp(overrides.pattern),
        message: 'Invalid format',
      };
    } catch {
      // Malformed pattern — ignore
    }
  }

  return rules;
}

/** Extract the default label for use in error messages */
function resolveDefaultLabel(field: FieldDef): string {
  return field.labels['default'] ?? field.canonical_name;
}
